import {
  ChangeDetectionStrategy, Component, DestroyRef, Injector, NgZone, OnInit, TemplateRef, ViewContainerRef, ViewEncapsulation, computed, effect,
  inject, signal, untracked, viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ConnectedPosition, OverlayRef, PositionStrategy, createFlexibleConnectedPositionStrategy, createGlobalPositionStrategy, createOverlayRef,
  createRepositionScrollStrategy,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Router } from '@angular/router';
import { CrwdIcon } from '@crewdle/ui-kit';
import { TranslateModule } from '@ngx-translate/core';
import { CoachService, coachDay, shouldAutoOpen } from 'src/app/services/coach/coach.service';
import { CoachActivityService, chatKey, conversationKey } from 'src/app/services/coach/coach-activity.service';
import { CoachMoment, TEAM_NOTIFIED_TIP } from 'src/app/services/coach/coach.models';
import { SupportChatbotService } from 'src/app/components/support-chatbot/support-chatbot.service';
import { SidebarListsService } from 'src/app/services/sidebar-lists/sidebar-lists.service';
import { ChatSidebarService } from 'src/app/services/chat-sidebar/chat-sidebar.service';

/** How often the auto-open rule is re-checked while a stuck moment waits. */
const AUTO_OPEN_TICK_MS = 1500;
/** The preview bubble hides itself after this long; the glow stays. */
const PREVIEW_VISIBLE_MS = 15000;
/** `?help=recap` (the weekly recap email's link) waits this long for the recap moment to load. */
const RECAP_LINK_WAIT_MS = 20000;
/**
 * While the bubble shows, how often it re-finds the Help button: the rail renders
 * late, leaves with the shell (code preview, share page), and the drawer slides it
 * on and off screen — none of which is a resize or a scroll.
 */
export const ANCHOR_CHECK_MS = 250;

/** The kit sidebar's Help button: the bubble's one anchor. */
export const HELP_BUTTON_SELECTOR = '.crwd-sidenav__help-btn';

/**
 * Beside the Help button with the bottoms lined up (the rail, expanded or collapsed),
 * 16px clear of it: past the rail's 8px inner padding and its edge, so the bubble
 * floats over the page instead of sitting on the rail border. Above the button
 * when there is no room on its right (the full-width phone drawer).
 */
export const HELP_BUTTON_POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 16 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8 },
];

/**
 * The one fallback when no Help button is on screen (drawer closed, no shell):
 * bottom-RIGHT, where the Help window opens. Never bottom-left — that is the
 * profile and organization button.
 */
export const FALLBACK_RIGHT = 'calc(1.25rem + var(--crwd-safe-right, 0px))';
export const FALLBACK_BOTTOM = 'calc(1.25rem + var(--crwd-safe-bottom, 0px))';

export type CoachPreviewPlacement = 'help-button' | 'fallback';

/** True when the address carries the recap email's `help=recap`. Pure — unit tested. */
export function isRecapLink(search: string): boolean {
  return new URLSearchParams(search).get('help') === 'recap';
}

/**
 * The Help button the bubble can point at: rendered AND on screen. The closed
 * drawer keeps its button in the DOM, translated off the left edge. Unit tested.
 */
export function visibleHelpButton(doc: Document, viewport: { innerWidth: number; innerHeight: number }): HTMLElement | null {
  for (const button of Array.from(doc.querySelectorAll<HTMLElement>(HELP_BUTTON_SELECTOR))) {
    const r = button.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && r.right > 0 && r.bottom > 0 && r.left < viewport.innerWidth && r.top < viewport.innerHeight) {
      return button;
    }
  }
  return null;
}

/** The small label over the bubble's line — the same names as the Help window's cards. */
export function previewLabelKey(moment: Pick<CoachMoment, 'kind' | 'tipId'>): string {
  if (moment.tipId === TEAM_NOTIFIED_TIP) return 'help.coach.team-notified.badge';
  if (moment.kind === 'recap') return 'help.coach.moment.badge-recap';
  if (moment.kind === 'levelUp') return 'help.coach.moment.badge-level-up';
  return 'help.coach.preview.label';
}

/**
 * The bubble's line. "Crewdle team notified" moments carry a one-sentence body that
 * says WHAT was reported (this problem / your request / your coaching request) and
 * what happens next; their preview alone ("The Crewdle team has been told") does not.
 */
export function previewText(moment: Pick<CoachMoment, 'tipId' | 'preview' | 'body'>): string {
  const body = moment.tipId === TEAM_NOTIFIED_TIP ? moment.body?.trim() : '';
  return body || moment.preview;
}

/**
 * What the moment is about, as the person knows it: the agent, else the agent or
 * chat its conversation belongs to (looked up in the rail's own lists). Null for
 * platform moments. Pure — unit tested.
 */
export function previewSubject(
  moment: Pick<CoachMoment, 'agentName' | 'conversationId' | 'chatId'>,
  agents: ReadonlyArray<{ conversationId: string; name: string }>,
  chats: ReadonlyArray<{ chatId: string; title: string }>,
): string | null {
  const named = moment.agentName?.trim();
  if (named) return named;
  if (moment.conversationId) {
    const agent = agents.find((a) => a.conversationId === moment.conversationId)?.name?.trim();
    if (agent) return agent;
  }
  if (moment.chatId) {
    const chat = chats.find((c) => c.chatId === moment.chatId)?.title?.trim();
    if (chat) return chat;
  }
  return null;
}

/**
 * The Help Coach's presence outside the Help window (contract §6): the Help
 * button's glow, the one-line preview bubble beside it, and the auto-open rule.
 * Lives in the app shell (tiny, no markdown) so the glow shows before the Help
 * panel's chunk is ever loaded.
 *
 * The bubble is a CDK overlay connected to the kit sidebar's Help button (the
 * same primitives the kit's tooltip uses; the kit's popover service is modal —
 * backdrop, closes on outside click — which a passive tip must not be). It
 * follows the button through scroll, resize and rail collapse, and falls back
 * to one fixed spot when the button is not on screen.
 */
@Component({
  selector: 'app-coach-launcher',
  standalone: true,
  imports: [TranslateModule, CrwdIcon],
  changeDetection: ChangeDetectionStrategy.Eager,
  // Unscoped (every selector is `coach-preview*`): the bubble renders in the CDK
  // overlay container, outside this host, so `:host-context(body.dark)` can't reach it.
  encapsulation: ViewEncapsulation.None,
  styles: [`
    app-coach-launcher { display: contents; }
    .coach-preview {
      display: flex;
      align-items: flex-start;
      gap: 0.25rem;
      box-sizing: border-box;
      width: max-content;
      max-width: min(22rem, calc(100vw - 2rem));
      padding: 0.75rem 0.5rem 0.75rem 0.875rem;
      border-radius: var(--radius-xl, 0.75rem);
      background: var(--background-color, #fff);
      color: var(--text-color);
      border: 1px solid var(--border-color, #e2e8f0);
      box-shadow: var(--shadow-xl, 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1));
      font-family: var(--font-body, 'Inter', system-ui, sans-serif);
      font-size: 0.875rem;
      line-height: 1.45;
    }
    /* Dark: the page is --background-color too, so lift the bubble the way the kit lifts the Help window. */
    body.dark .coach-preview {
      background: var(--surface-color, #242526);
      box-shadow: 0 24px 48px -12px rgb(0 0 0 / 0.7), 0 8px 16px -8px rgb(0 0 0 / 0.5);
    }
    .coach-preview-open {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding: 0;
      border: none;
      border-radius: 0.375rem;
      background: none;
      color: inherit;
      font: inherit;
      text-align: start;
      cursor: pointer;
    }
    .coach-preview-head {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      min-width: 0;
      color: var(--text-secondary-color, #475569);
      font-size: 0.75rem;
      font-weight: 600;
      line-height: 1rem;
    }
    .coach-preview-icon { flex: none; color: var(--accent-color, #4c17ca); }
    .coach-preview-label { flex: none; }
    .coach-preview-subject { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
    .coach-preview-text { overflow-wrap: anywhere; }
    .coach-preview-sr {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
    }
    .coach-preview-open:focus-visible, .coach-preview-close:focus-visible { outline: 2px solid var(--accent-color); outline-offset: 2px; }
    .coach-preview-close {
      flex: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      padding: 0;
      border: none;
      border-radius: 0.375rem;
      background: none;
      color: var(--text-secondary-color, #475569);
      cursor: pointer;
    }
    .coach-preview-close:hover { background: var(--surface-hover, var(--surface-color)); color: var(--text-color); }
  `],
  template: `
    <ng-template #bubble>
      @if (previewMoment(); as moment) {
        <div
          class="coach-preview"
          role="status"
          data-testid="coach-preview"
          [attr.data-placement]="placement()"
          (keydown.escape)="hidePreview(moment)"
        >
          <button type="button" class="coach-preview-open" data-testid="coach-preview-open" (click)="openMoment(moment)">
            <span class="coach-preview-head">
              <crwd-icon class="coach-preview-icon" name="lightbulb" size="1rem" data-testid="coach-preview-icon" />
              <span class="coach-preview-label" data-testid="coach-preview-label">{{ labelKey() | translate }}</span>
              @if (subject(); as subject) {
                <span class="coach-preview-subject" data-testid="coach-preview-subject">· {{ subject }}</span>
              }
            </span>
            <span class="coach-preview-text" data-testid="coach-preview-text">{{ text() }}</span>
            <span class="coach-preview-sr">{{ 'help.coach.preview.open' | translate }}</span>
          </button>
          <button
            type="button"
            class="coach-preview-close"
            data-testid="coach-preview-close"
            [attr.aria-label]="'help.coach.preview.hide' | translate"
            (click)="hidePreview(moment)"
          >
            <crwd-icon name="x" size="1rem" />
          </button>
        </div>
      }
    </ng-template>
  `,
})
export class CoachLauncherComponent implements OnInit {
  private readonly coach = inject(CoachService);
  private readonly activity = inject(CoachActivityService);
  private readonly support = inject(SupportChatbotService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly zone = inject(NgZone);
  /** Captured at boot, before routing can rewrite the address. */
  private readonly recapLinkPending = signal(typeof location !== 'undefined' && isRecapLink(location.search));

  /** Moments whose bubble the person closed (×) — the moment itself stays in Help. */
  private readonly hiddenPreviews = signal<ReadonlySet<string>>(new Set());
  private readonly expiredPreviews = signal<ReadonlySet<string>>(new Set());
  private readonly helpOpen = signal(false);

  /** The rail's own lists (already live for the sidebar), to name the agent or chat a moment is about. */
  private readonly lists = inject(SidebarListsService);
  private readonly chatLists = inject(ChatSidebarService);
  private readonly agents = toSignal(this.lists.agents$, { initialValue: null });
  private readonly sharedAgents = toSignal(this.lists.sharedAgents$, { initialValue: null });
  private readonly chatRoster = toSignal(this.chatLists.roster$, { initialValue: null });
  private readonly folderChats = toSignal(this.chatLists.folderChats$, { initialValue: {} });

  private readonly bubble = viewChild<TemplateRef<unknown>>('bubble');
  private overlayRef: OverlayRef | null = null;
  /** The Help button the overlay is connected to; null while on the fallback. */
  private anchor: HTMLElement | null = null;
  private anchorObserver: ResizeObserver | null = null;
  private anchorTimer: ReturnType<typeof setInterval> | null = null;
  private readonly onViewportResize = () => this.syncAnchor();

  /** Where the bubble sits right now (exposed as `data-placement` for tests and QA). */
  readonly placement = signal<CoachPreviewPlacement>('fallback');

  readonly glowing = computed(() => !!this.coach.activeMoment() && !this.coach.optedOut());

  readonly previewMoment = computed(() => {
    const moment = this.coach.activeMoment();
    if (!moment || this.coach.optedOut() || this.helpOpen()) return null;
    if (this.hiddenPreviews().has(moment.id) || this.expiredPreviews().has(moment.id)) return null;
    return moment;
  });

  readonly labelKey = computed(() => {
    const moment = this.previewMoment();
    return moment ? previewLabelKey(moment) : 'help.coach.preview.label';
  });

  readonly text = computed(() => {
    const moment = this.previewMoment();
    return moment ? previewText(moment) : '';
  });

  readonly subject = computed(() => {
    const moment = this.previewMoment();
    if (!moment) return null;
    const chats = [
      ...(this.chatRoster() ?? []).flatMap((row) => (row.chat ? [row.chat] : [])),
      ...Object.values(this.folderChats()).flat(),
    ];
    return previewSubject(moment, [...(this.agents() ?? []), ...(this.sharedAgents() ?? [])], chats);
  });

  constructor() {
    effect(() => {
      const on = this.glowing();
      if (typeof document !== 'undefined') document.documentElement.classList.toggle('coach-has-tip', on);
    });
    // The recap email's link opens Help on the newest recap moment, then drops the param.
    effect(() => {
      if (!this.recapLinkPending()) return;
      const recap = this.coach.moments().find((m) => m.kind === 'recap');
      if (recap) this.resolveRecapLink(recap);
    });
    // The overlay lives exactly as long as there is a preview to show.
    effect(() => {
      const showing = !!this.previewMoment();
      const template = this.bubble();
      untracked(() => (showing && template ? this.openBubble(template) : this.closeBubble()));
    });
    effect(() => {
      const moment = this.previewMoment();
      if (!moment) return;
      const timer = setTimeout(() => this.expiredPreviews.update((s) => new Set(s).add(moment.id)), PREVIEW_VISIBLE_MS);
      return () => clearTimeout(timer);
    });
    this.destroyRef.onDestroy(() => this.closeBubble());
  }

  ngOnInit(): void {
    this.coach.start();
    if (this.recapLinkPending()) {
      const timer = setTimeout(() => this.resolveRecapLink(null), RECAP_LINK_WAIT_MS);
      this.destroyRef.onDestroy(() => clearTimeout(timer));
    }
    const sub = this.support.open$.subscribe((open) => this.helpOpen.set(open));
    const timer = setInterval(() => this.checkAutoOpen(), AUTO_OPEN_TICK_MS);
    this.destroyRef.onDestroy(() => {
      sub.unsubscribe();
      clearInterval(timer);
      if (typeof document !== 'undefined') document.documentElement.classList.remove('coach-has-tip');
    });
  }

  openMoment(moment: CoachMoment): void {
    this.coach.focus(moment);
    void this.coach.markShown(moment);
    this.support.openPanel();
  }

  private resolveRecapLink(recap: CoachMoment | null): void {
    if (!this.recapLinkPending()) return;
    this.recapLinkPending.set(false);
    if (recap) {
      this.openMoment(recap);
    } else {
      this.support.openPanel();
    }
    void this.router.navigate([], { queryParams: { help: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  hidePreview(moment: CoachMoment): void {
    this.hiddenPreviews.update((s) => new Set(s).add(moment.id));
  }

  /** The auto-open rule, evaluated against the live screen. */
  checkAutoOpen(now = new Date()): void {
    const moment = this.coach.activeMoment();
    if (!moment?.autoOpen || this.coach.optedOut()) return;
    const context = this.activity.currentContext();
    const currentKey = context.chatId ? chatKey(context.chatId) : context.conversationId ? conversationKey(context.conversationId) : null;
    const surface = currentKey ? this.activity.surface(currentKey) : undefined;
    const open = shouldAutoOpen({
      moment,
      today: coachDay(now),
      lastAutoOpenDay: this.coach.lastAutoOpenDay(),
      currentSurfaceKey: currentKey,
      surfaceBusy: surface ? surface.isBusy() : false,
      typing: this.activity.isTyping(now.getTime()),
      helpOpen: this.helpOpen(),
    });
    if (!open) return;
    this.coach.recordAutoOpen(coachDay(now));
    this.openMoment(moment);
  }

  /**
   * Re-find the Help button and keep the bubble on it: switch between the connected
   * and the fallback strategy when the button comes or goes, otherwise re-measure.
   */
  syncAnchor(): void {
    if (!this.overlayRef || typeof document === 'undefined') return;
    const button = visibleHelpButton(document, window);
    if (button === this.anchor) {
      this.overlayRef.updatePosition();
      return;
    }
    this.anchor = button;
    this.zone.runOutsideAngular(() => this.observeAnchor(button));
    this.placement.set(button ? 'help-button' : 'fallback');
    this.overlayRef.updatePositionStrategy(this.strategyFor(button));
  }

  private openBubble(template: TemplateRef<unknown>): void {
    if (this.overlayRef || typeof document === 'undefined') return;
    this.anchor = visibleHelpButton(document, window);
    this.placement.set(this.anchor ? 'help-button' : 'fallback');
    this.overlayRef = createOverlayRef(this.injector, {
      positionStrategy: this.strategyFor(this.anchor),
      // CDK re-applies a connected position on window resize; this covers scrolling.
      scrollStrategy: createRepositionScrollStrategy(this.injector),
      panelClass: 'coach-preview-pane',
    });
    this.overlayRef.attach(new TemplatePortal(template, this.viewContainerRef));
    // Outside the zone: these only move the overlay; `placement` is a signal, so a
    // switch still refreshes the bubble without an app-wide check every tick.
    this.zone.runOutsideAngular(() => {
      this.observeAnchor(this.anchor);
      window.addEventListener('resize', this.onViewportResize);
      this.anchorTimer = setInterval(() => this.syncAnchor(), ANCHOR_CHECK_MS);
    });
  }

  private closeBubble(): void {
    if (this.anchorTimer !== null) clearInterval(this.anchorTimer);
    this.anchorTimer = null;
    this.observeAnchor(null);
    if (typeof window !== 'undefined') window.removeEventListener('resize', this.onViewportResize);
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.anchor = null;
  }

  private strategyFor(button: HTMLElement | null): PositionStrategy {
    if (!button) return createGlobalPositionStrategy(this.injector).right(FALLBACK_RIGHT).bottom(FALLBACK_BOTTOM);
    return createFlexibleConnectedPositionStrategy(this.injector, button)
      .withPositions(HELP_BUTTON_POSITIONS)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(8);
  }

  /** The rail collapsing or expanding resizes the button without resizing the window. */
  private observeAnchor(button: HTMLElement | null): void {
    this.anchorObserver?.disconnect();
    this.anchorObserver = null;
    if (!button || typeof ResizeObserver === 'undefined') return;
    this.anchorObserver = new ResizeObserver(() => this.overlayRef?.updatePosition());
    this.anchorObserver.observe(button);
  }
}
